import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ICliente } from 'modelos/src';
import { Subscription } from 'rxjs';
import { AuxiliaresModule } from '../../../../../auxiliares/auxiliares.module';
import {
  IColumnas,
  ICrearBoton,
} from '../../../../../auxiliares/tabla/tabla.component';
import { DialogService } from '../../../../../auxiliares/dialog/dialog.service';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { Lista2Service } from '../../../../../auxiliares/servicios/lista2.service';
import { ClientesService } from '../../../../../auxiliares/servicios/http/clientes.service';
import { LoginService } from '../../../../login/login.service';
import { esc } from '../presentacion-entidades';
import { estadoHtml } from '../../../../../auxiliares/tabla/celdas';

interface ISolicitanteFila {
  nombre: string;
  predeterminado: boolean;
}

// No se edita ni se borra: es el solicitante genérico de las llamadas.
const PARTICULAR = 'Particular';

/**
 * Solicitantes de emergencias del cliente propio: strings en
 * `cliente.config.solicitantesEmergencias` + `solicitantePredeterminado`.
 * Se guardan enteros con PUT /clientes/updateSolicitante/:id.
 */
@Component({
  selector: 'app-solicitantes-emergencias-medicas',
  imports: [AuxiliaresModule],
  templateUrl: './solicitantes.component.html',
})
export class SolicitantesComponent implements OnInit, OnDestroy {
  helper = inject(HelperService);
  private clientesService = inject(ClientesService);
  private dialogService = inject(DialogService);
  private lista2 = inject(Lista2Service);

  public name = 'SolicitantesComponent';
  private idCliente = LoginService.getCliente()?._id;
  private cliente$?: Subscription;
  private guardando = false;

  private solicitantes: string[] = [];
  private predeterminado = '';
  public datos?: ISolicitanteFila[];
  public totalCount = 0;
  public columnas: IColumnas<ISolicitanteFila>[] = [];

  public botonCrear: ICrearBoton = {
    mostrar: this.helper.tienePermiso(
      'Emergencias Médicas - Crear solicitantes',
    ),
    tooltip: 'Agregar solicitante',
    color: 'primary',
    tipo: 'material',
    icon: 'add',
    accion: () => this.agregar(),
  };

  private puedeEditar = () =>
    this.helper.tienePermiso('Emergencias Médicas - Editar solicitantes');
  private puedeEliminar = () =>
    this.helper.tienePermiso('Emergencias Médicas - Eliminar solicitantes');

  // Datos

  private listar(): void {
    if (!this.idCliente) return;
    this.cliente$?.unsubscribe();
    this.cliente$ = this.lista2.cliente(this.idCliente, 30).subscribe({
      next: (cliente: ICliente) => {
        if (this.guardando) return;
        this.solicitantes = [
          ...(cliente?.config?.solicitantesEmergencias ?? []),
        ];
        this.predeterminado = cliente?.config?.solicitantePredeterminado ?? '';
        this.armarFilas();
      },
      error: (error) => {
        console.error(error);
        this.helper.notifError(error);
      },
    });
  }

  /** El predeterminado primero; el resto en orden alfabético. */
  private armarFilas(): void {
    const filas = [...this.solicitantes]
      .sort((a, b) => {
        if (a === this.predeterminado) return -1;
        if (b === this.predeterminado) return 1;
        return a.localeCompare(b, 'es');
      })
      .map((nombre) => ({
        nombre,
        predeterminado: nombre === this.predeterminado,
      }));
    if (HelperService.cambiaronLosDatos(this.datos, filas)) {
      this.datos = filas;
    }
    this.totalCount = filas.length;
  }

  private existe(nombre: string, salvo?: string): boolean {
    const n = nombre.toLocaleLowerCase('es');
    return this.solicitantes.some(
      (s) => s !== salvo && s.toLocaleLowerCase('es') === n,
    );
  }

  private async guardar(
    solicitantes: string[],
    predeterminado: string,
    mensaje: string,
  ): Promise<void> {
    if (!this.idCliente) return;
    this.guardando = true;
    try {
      await this.clientesService.updateSolicitantes(this.idCliente, {
        config: {
          solicitantesEmergencias: solicitantes,
          solicitantePredeterminado: predeterminado,
        },
      });
      this.solicitantes = solicitantes;
      this.predeterminado = predeterminado;
      this.armarFilas();
      this.helper.notifSuccess(mensaje);
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    } finally {
      this.guardando = false;
    }
  }

  // Acciones

  public async agregar(): Promise<void> {
    const res = await this.dialogService.prompt(
      'Agregar solicitante',
      'Ingresá el nombre del solicitante (por ejemplo, una institución o un servicio de emergencias).',
      'Nombre',
      'Agregar',
      'Cancelar',
    );
    const nombre = typeof res === 'string' ? res.trim() : '';
    if (!nombre) return;
    if (this.existe(nombre)) {
      this.helper.notifError(
        `Ya existe el solicitante "${nombre}"`,
        'Cerrar',
        true,
      );
      return;
    }
    await this.guardar(
      [...this.solicitantes, nombre],
      this.predeterminado || nombre,
      'Solicitante agregado',
    );
  }

  public async editar(fila: ISolicitanteFila): Promise<void> {
    const res = await this.dialogService.prompt(
      'Editar solicitante',
      'Ingresá el nuevo nombre del solicitante.',
      'Nombre',
      'Guardar',
      'Cancelar',
      'text',
      fila.nombre,
    );
    const nombre = typeof res === 'string' ? res.trim() : '';
    if (!nombre || nombre === fila.nombre) return;
    if (this.existe(nombre, fila.nombre)) {
      this.helper.notifError(
        `Ya existe el solicitante "${nombre}"`,
        'Cerrar',
        true,
      );
      return;
    }
    await this.guardar(
      this.solicitantes.map((s) => (s === fila.nombre ? nombre : s)),
      this.predeterminado === fila.nombre ? nombre : this.predeterminado,
      'Solicitante actualizado',
    );
  }

  public async marcarPredeterminado(fila: ISolicitanteFila): Promise<void> {
    if (fila.predeterminado) return;
    await this.guardar(
      this.solicitantes,
      fila.nombre,
      `"${fila.nombre}" es ahora el solicitante predeterminado`,
    );
  }

  public async eliminar(fila: ISolicitanteFila): Promise<void> {
    const ok = await this.dialogService.confirm(
      'Eliminar solicitante',
      `¿Confirmás eliminar el solicitante <strong>${esc(fila.nombre)}</strong>? Las emergencias ya registradas lo conservan.`,
    );
    if (!ok) return;
    const restantes = this.solicitantes.filter((s) => s !== fila.nombre);
    await this.guardar(
      restantes,
      this.predeterminado === fila.nombre
        ? (restantes[0] ?? '')
        : this.predeterminado,
      'Solicitante eliminado',
    );
  }

  // Tabla

  private setColumnas(): void {
    this.columnas = [
      {
        header: { label: 'Solicitante' },
        row: {
          html: (f) => {
            let html = `<div class="tabla-celda-fila">`;
            html += `<span class="material-symbols-outlined tabla-icono-dato">person_raised_hand</span>`;
            html += `<div class="tabla-celda-col">`;
            html += `<span class="tabla-principal">${esc(f.nombre)}</span>`;
            if (f.predeterminado) {
              html += estadoHtml({ texto: 'Predeterminado', tono: 'info' });
              html += `<span class="tabla-secundario">Se propone al registrar una emergencia</span>`;
            } else if (f.nombre === PARTICULAR) {
              html += `<span class="tabla-secundario">Llamadas de particulares</span>`;
            }
            html += `</div></div>`;
            return html;
          },
        },
      },
      {
        header: { label: 'Acciones' },
        row: {
          // Menú en el orden de siempre; Eliminar al final
          accionesGrouped: [
            {
              tipo: 'material',
              icon: 'star',
              tooltip: 'Marcar como predeterminado',
              click: (f) => this.marcarPredeterminado(f),
              oculta: (f) => f.predeterminado,
              disabled: () => !this.puedeEditar(),
            },
            {
              tipo: 'material',
              icon: 'edit',
              tooltip: 'Editar',
              click: (f) => this.editar(f),
              disabled: (f) => f.nombre === PARTICULAR || !this.puedeEditar(),
            },
            {
              tipo: 'material',
              color: 'warn',
              icon: 'delete',
              tooltip: 'Eliminar',
              click: (f) => this.eliminar(f),
              disabled: (f) => f.nombre === PARTICULAR || !this.puedeEliminar(),
            },
          ],
        },
      },
    ];
  }

  // Hooks

  ngOnInit(): void {
    this.setColumnas();
    this.listar();
  }

  ngOnDestroy(): void {
    this.cliente$?.unsubscribe();
  }
}
